import { useEffect, useState } from 'react';
import { api, fmtDate, type AttributionRule, type GradeTier, type PlatformConfig } from '../api';
import { useAction, useAsync } from '../hooks';

const ORDER_TYPE_LABEL: Record<string, string> = {
  NEW_CLIENT: 'Новый клиент (первая покупка)',
  NEW_CLIENT_IN_YEAR: 'Новый клиент в рамках учебного года',
  UPSELL: 'Доп. продажа',
  AWAKE: 'Эвейк (возврат ушедшего клиента)',
  RENEWAL: 'Продление',
  UNKNOWN: 'Тип не распознан',
};

/**
 * Экран настроек — центр требования ТЗ §7 «всё настраивается без релизов».
 *
 * Работа в два шага: правки копятся в черновике, публикация создаёт новую
 * версию. До публикации ничего в бою не меняется, а перед ней видно, что
 * именно уедет.
 */
export function ConfigPage() {
  const { data, loading, error, reload } = useAsync(() => api.config(), []);
  const [draft, setDraft] = useState<PlatformConfig | null>(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (data) setDraft(structuredClone(data.draft.config));
  }, [data]);

  const save = useAction(async (c: PlatformConfig) => {
    await api.saveDraft(c);
    reload();
  });
  const publish = useAction(async (c: string) => {
    await api.publish(c);
    setComment('');
    reload();
  });

  if (loading || !draft || !data) return <p className="subtitle">{error ?? 'Загружаем…'}</p>;

  const patch = (fn: (c: PlatformConfig) => void) => {
    const next = structuredClone(draft);
    fn(next);
    setDraft(next);
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.draft.config);
  const hasPending = data.diff.length > 0;

  return (
    <>
      <h1>Настройки начислений</h1>
      <p className="subtitle">
        Действующая версия {data.current.version}, опубликована {fmtDate(data.current.publishedAt)}. Черновик менял{' '}
        {data.draft.updatedBy}, {fmtDate(data.draft.updatedAt)}.
      </p>

      {save.error && <div className="notice err">{save.error}</div>}
      {publish.error && <div className="notice err">{publish.error}</div>}
      {publish.ok && <div className="notice ok">Настройки опубликованы</div>}

      {/* ─── Атрибуция (ТЗ §3.2–3.4) ─── */}
      <h2>Какие заказы дают бонусы</h2>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Тип заказа</th>
              <th>Начисляем</th>
              <th className="num">Доля от ставки, %</th>
              <th className="num">Мин. сумма заказа, ₽</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody>
            {draft.attribution.rules.map((rule, i) => (
              <tr key={rule.orderType}>
                <td>
                  {ORDER_TYPE_LABEL[rule.orderType] ?? rule.orderType}
                  {rule.pendingClarification && (
                    <>
                      {' '}
                      <span className="badge warn" title="Бизнес ещё не дал ответ — ТЗ §3.3">
                        нужно решение
                      </span>
                    </>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={rule.accrues}
                    onChange={(e) => patch((c) => void (c.attribution.rules[i]!.accrues = e.target.checked))}
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    value={rule.ratePerMille / 10}
                    onChange={(e) =>
                      patch((c) => void (c.attribution.rules[i]!.ratePerMille = Math.round(Number(e.target.value) * 10)))
                    }
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    min={0}
                    value={rule.minOrderAmountKopecks / 100}
                    onChange={(e) =>
                      patch(
                        (c) =>
                          void (c.attribution.rules[i]!.minOrderAmountKopecks = Math.round(Number(e.target.value) * 100)),
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    value={rule.comment}
                    onChange={(e) => patch((c) => void (c.attribution.rules[i]!.comment = e.target.value))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="grid cols-2" style={{ marginTop: 16 }}>
          <div className="field">
            <label>Как определяется реферер</label>
            <select
              value={draft.attribution.refererResolution}
              onChange={(e) =>
                patch((c) => void (c.attribution.refererResolution = e.target.value as PlatformConfig['attribution']['refererResolution']))
              }
            >
              <option value="AS_REPORTED">Как посчитала основная платформа</option>
              <option value="FIRST_TOUCH">Первый реферер приглашённого</option>
              <option value="LAST_TOUCH">Владелец ссылки на конкретном заказе</option>
            </select>
          </div>
          <div className="field">
            <label>Заказы с нераспознанным типом</label>
            <select
              value={String(draft.attribution.quarantineUnknownOrderTypes)}
              onChange={(e) => patch((c) => void (c.attribution.quarantineUnknownOrderTypes = e.target.value === 'true'))}
            >
              <option value="true">Отправлять в карантин</option>
              <option value="false">Обрабатывать по правилу «не распознан»</option>
            </select>
          </div>
        </div>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={draft.attribution.forbidSelfReferral}
            onChange={(e) => patch((c) => void (c.attribution.forbidSelfReferral = e.target.checked))}
          />
          Не начислять за покупку по собственной ссылке
        </label>
      </div>

      {/* ─── Шкала (ТЗ §4) ─── */}
      <h2>Грейдовая шкала</h2>
      <div className="card">
        <p className="subtitle" style={{ marginTop: 0 }}>
          Ступени должны идти подряд от 1-го приглашённого, последняя — открытая («и далее»). Изменение шкалы не
          пересчитывает уже сделанные начисления.
        </p>
        <ScaleEditor value={draft.accrual.gradeScale} onChange={(scale) => patch((c) => void (c.accrual.gradeScale = scale))} />
      </div>

      {/* ─── Холд и счётчик ─── */}
      <h2>Холд и счётчик приглашённых</h2>
      <div className="card">
        <div className="grid cols-2">
          <div className="field">
            <label>Срок холда, дней (ТЗ §3.5)</label>
            <input
              type="number"
              min={0}
              max={365}
              value={draft.accrual.holdDays}
              onChange={(e) => patch((c) => void (c.accrual.holdDays = Number(e.target.value)))}
            />
          </div>
          <div className="field">
            <label>Счётчик приглашённых (ТЗ §4.4)</label>
            <select
              value={draft.accrual.counterScope}
              onChange={(e) => patch((c) => void (c.accrual.counterScope = e.target.value as 'ALL_TIME' | 'ACADEMIC_YEAR'))}
            >
              <option value="ALL_TIME">Копится за всё время</option>
              <option value="ACADEMIC_YEAR">Обнуляется каждый учебный год</option>
            </select>
          </div>
          <div className="field">
            <label>Когда приглашённый занимает место (ТЗ §4.3)</label>
            <select
              value={draft.accrual.counterAdvance}
              onChange={(e) => patch((c) => void (c.accrual.counterAdvance = e.target.value as 'ON_RELEASE' | 'ON_PAID'))}
            >
              <option value="ON_RELEASE">После прохождения холда</option>
              <option value="ON_PAID">Сразу при оплате</option>
            </select>
          </div>
          <div className="field">
            <label>Что считает шкала</label>
            <select
              value={draft.accrual.tierBasis}
              onChange={(e) => patch((c) => void (c.accrual.tierBasis = e.target.value as 'UNIQUE_REFEREE' | 'QUALIFYING_ORDER'))}
            >
              <option value="UNIQUE_REFEREE">Уникальных приглашённых</option>
              <option value="QUALIFYING_ORDER">Каждый зачётный заказ</option>
            </select>
          </div>
          <div className="field">
            <label>Возврат после того, как бонусы потрачены (ТЗ §3.5)</label>
            <select
              value={draft.accrual.clawbackPolicy}
              onChange={(e) =>
                patch((c) => void (c.accrual.clawbackPolicy = e.target.value as PlatformConfig['accrual']['clawbackPolicy']))
              }
            >
              <option value="ALLOW_NEGATIVE">Увести баланс в минус, гасить будущими начислениями</option>
              <option value="CLAMP_TO_ZERO">Списать сколько есть, остаток простить</option>
              <option value="MANUAL_ONLY">Не трогать баланс, разбирать вручную</option>
            </select>
          </div>
          <div className="field">
            <label>Начало учебного года</label>
            <div className="row">
              <input
                type="number"
                min={1}
                max={31}
                value={draft.accrual.academicYearStart.day}
                onChange={(e) => patch((c) => void (c.accrual.academicYearStart.day = Number(e.target.value)))}
              />
              <select
                value={draft.accrual.academicYearStart.month}
                onChange={(e) => patch((c) => void (c.accrual.academicYearStart.month = Number(e.target.value)))}
              >
                {['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'].map(
                  (m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ),
                )}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Лавка ─── */}
      <h2>Лавка</h2>
      <div className="card">
        <div className="grid cols-3">
          <div className="field">
            <label>Глобальный минимум цены, бонусов</label>
            <input
              type="number"
              min={0}
              value={draft.shop.globalMinPriceBonuses}
              onChange={(e) => patch((c) => void (c.shop.globalMinPriceBonuses = Number(e.target.value)))}
            />
          </div>
          <div className="field">
            <label>Срок жизни промокода, дней (ТЗ §6.3)</label>
            <input
              type="number"
              min={1}
              value={draft.shop.promoTtlDays}
              onChange={(e) => patch((c) => void (c.shop.promoTtlDays = Number(e.target.value)))}
            />
          </div>
          <div className="field">
            <label>Откуда берутся промокоды (ТЗ §6.4)</label>
            <select
              value={draft.shop.promoProvider}
              onChange={(e) => patch((c) => void (c.shop.promoProvider = e.target.value as 'POOL' | 'ON_DEMAND'))}
            >
              <option value="POOL">Пул, загруженный админом</option>
              <option value="ON_DEMAND">Выпуск по запросу в основной платформе</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── Публикация ─── */}
      <div className="sticky-actions">
        {hasPending && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="row between">
              <strong>Не опубликовано: {data.diff.length} изменени(й)</strong>
            </div>
            <div className="diff" style={{ marginTop: 10 }}>
              {data.diff.slice(0, 20).map((d) => (
                <div key={d.path}>
                  <span className="path">{d.path}</span>: <span className="from">{JSON.stringify(d.from)}</span> →{' '}
                  <span className="to">{JSON.stringify(d.to)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="row">
          <button disabled={!dirty || save.busy} onClick={() => void save.run(draft)}>
            {save.busy ? 'Сохраняем…' : 'Сохранить черновик'}
          </button>
          <input
            placeholder="Комментарий к публикации"
            style={{ maxWidth: 320 }}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            className="secondary"
            disabled={dirty || !hasPending || publish.busy}
            title={dirty ? 'Сначала сохраните черновик' : undefined}
            onClick={() => void publish.run(comment)}
          >
            {publish.busy ? 'Публикуем…' : 'Опубликовать'}
          </button>
        </div>
      </div>

      <VersionsTable onRollback={reload} />
    </>
  );
}

function ScaleEditor({ value, onChange }: { value: GradeTier[]; onChange: (v: GradeTier[]) => void }) {
  /**
   * Границы ступеней пересобираются автоматически: администратор задаёт только
   * «сколько приглашённых в ступени» и сумму, а связность (без дыр и
   * пересечений) поддерживается редактором. Ошибиться руками в таком месте
   * слишком легко, а цена ошибки — неверные выплаты.
   */
  const rebuild = (tiers: { size: number | null; bonuses: number }[]): GradeTier[] => {
    let from = 1;
    return tiers.map((t, i) => {
      const isLast = i === tiers.length - 1;
      const to = isLast || t.size === null ? null : from + t.size - 1;
      const tier: GradeTier = { fromOrdinal: from, toOrdinal: to, bonuses: t.bonuses };
      if (to !== null) from = to + 1;
      return tier;
    });
  };

  const asSizes = value.map((t) => ({
    size: t.toOrdinal === null ? null : t.toOrdinal - t.fromOrdinal + 1,
    bonuses: t.bonuses,
  }));

  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Ступень</th>
            <th className="num">Сколько приглашённых</th>
            <th className="num">Бонусов за каждого</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {value.map((tier, i) => (
            <tr key={i}>
              <td>
                {tier.toOrdinal === null
                  ? `${tier.fromOrdinal}-й и далее`
                  : tier.fromOrdinal === tier.toOrdinal
                    ? `${tier.fromOrdinal}-й`
                    : `${tier.fromOrdinal}–${tier.toOrdinal}-й`}
              </td>
              <td className="num">
                {tier.toOrdinal === null ? (
                  <span className="muted">все остальные</span>
                ) : (
                  <input
                    type="number"
                    min={1}
                    value={tier.toOrdinal - tier.fromOrdinal + 1}
                    onChange={(e) => {
                      const next = [...asSizes];
                      next[i] = { ...next[i]!, size: Math.max(1, Number(e.target.value)) };
                      onChange(rebuild(next));
                    }}
                  />
                )}
              </td>
              <td className="num">
                <input
                  type="number"
                  min={0}
                  value={tier.bonuses}
                  onChange={(e) => {
                    const next = [...asSizes];
                    next[i] = { ...next[i]!, bonuses: Math.max(0, Number(e.target.value)) };
                    onChange(rebuild(next));
                  }}
                />
              </td>
              <td>
                {value.length > 1 && (
                  <button
                    className="secondary small"
                    onClick={() => onChange(rebuild(asSizes.filter((_, j) => j !== i)))}
                  >
                    Удалить
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        className="secondary small"
        style={{ marginTop: 12 }}
        onClick={() => {
          // Новая ступень вставляется перед открытой: открытая всегда последняя.
          const next = [...asSizes];
          next.splice(next.length - 1, 0, { size: 1, bonuses: next[next.length - 1]?.bonuses ?? 500 });
          onChange(rebuild(next));
        }}
      >
        Добавить ступень
      </button>
    </>
  );
}

function VersionsTable({ onRollback }: { onRollback: () => void }) {
  const { data, reload } = useAsync(() => api.versions(), []);
  const rollback = useAction(async (v: number) => {
    await api.rollback(v);
    reload();
    onRollback();
  });

  return (
    <>
      <h2>История версий</h2>
      {rollback.error && <div className="notice err">{rollback.error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th className="num">Версия</th>
              <th>Опубликовал</th>
              <th>Комментарий</th>
              <th>Когда</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((v) => (
              <tr key={v.version}>
                <td className="num">
                  {v.version} {v.is_current && <span className="badge on">текущая</span>}
                </td>
                <td>{v.published_by}</td>
                <td>{v.comment || '—'}</td>
                <td>{fmtDate(v.published_at)}</td>
                <td>
                  {!v.is_current && (
                    <button className="secondary small" disabled={rollback.busy} onClick={() => void rollback.run(v.version)}>
                      Откатиться
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export type { AttributionRule };
